import * as React from 'preact/compat'
import SVG from '../SVG'
import Api from '../../api'
import cn from 'classnames'
import formCss from '../../../css/form.pcss.json'
import buttonsCss from '../../../css/buttons.pcss.json'
import { captureFetchException } from '../../common/helpers'
import { showToast } from '../toast'

interface PropTypes {
  suggestion: {
    id: number,
    value: string | {
      action: 'add' | 'delete',
      value: string
    },
    noAction?: boolean,
    user: {
      url: string,
      name: string,
      avatar: string,
      inTeam: boolean,
      credibility?: number
    },
    isOwner: boolean
  },
  field: string,
  altField: string,
  onAccept: (id: number, altField: string, field: string, value: any) => void,
  onReject: (id: number, altField: string) => void,
  isEditor: boolean,
  showFieldName?: boolean
  showCredibility?: boolean
}

const fieldNames = {
  'notes': 'замечание',
  'GameName': 'название',
  'titles': 'название',
  'square_poster': 'квадратную обложку',
  'large_poster': 'шапку',
  'GameDate': 'дату',
  'GamePlatform': 'платформу',
  'GameJanr': 'тег',
  'GameCompany': 'компанию',
  'GamePublishers': 'издателя',
  'GameDevelopers': 'разработчика',
  'GameSite': 'сайт',
  'GameRusSite': 'русский сайт',
  'steam_id': 'страницу в Steam'
}

export default class GameFixSuggestionLine extends React.Component<PropTypes, any> {

  renderVerb = () => {
    const { value } = this.props.suggestion;
    let verb = 'предлагает'
    let fieldName = ''
    if (this.props.showFieldName) {
      fieldName += ' ' + fieldNames[this.props.altField]
    }

    if (typeof value === 'string') {
      return <span>{verb}{fieldName}</span>;
    }

    if (value.action === 'add') {
      verb += ' <span style="color: var(--green)">добавить</span>';
    }
    if (value.action === 'delete') {
      verb += ' <span style="color: var(--brand-red)">удалить</span>';
    }
    verb += fieldName
    return <span dangerouslySetInnerHTML={{ __html: verb }}/>
  }

  renderValue = () => {
    const { value } = this.props.suggestion;
    const { field } = this.props;

    if (['square_poster', 'large_poster'].includes(field) && typeof value === 'string') {
      return <a href={value} data-plugins='lightbox'>
        <img src={value} alt='' style='max-height: 150px; border-radius: 8px'/>
      </a>;
    }

    if (field === 'notes' && typeof value === 'string') {
      const replacer = (match: string) => {
        return `<a href="${match}" target="_blank">${match}</a>`
      }
      const urlRegex = /https?:\/\/[a-z\dа-яё.\/\-_?=&#%,+:@]+/gi
      const newValue = value.replace(urlRegex, replacer)
      return <div style={{ width: '100%', color: 'var(--fg-color)' }} dangerouslySetInnerHTML={{ __html: newValue }}/>
    }

    if (typeof value === 'string') {
      return <span dangerouslySetInnerHTML={{ __html: value }} style='color: var(--fg-color)'/>;
    }
    return <span dangerouslySetInnerHTML={{ __html: value.value }} style='color: var(--fg-color)'/>;
  }

  accept = (append: boolean | React.TargetedEvent = false) => {
    const { altField, field, suggestion, onAccept } = this.props
    const { id } = suggestion;

    let url = `game-suggestion/accept/${id}`
    if (typeof append === 'boolean' && append) {
      url += '?append=true'
    }
    if (['square_poster', 'large_poster'].includes(field)) {
      url = `game-suggestion/accept-image/${id}`
    }

    Api.post({ url })
      .then((result) => {
        onAccept(id, altField, field, result)
      })
      .catch(err => {
        captureFetchException(err)
        showToast({
          type: 'error',
          text: 'Не удалось принять предложение. Попробуй позже.',
        })
      })
  }

  reject = () => {
    const { altField, field, suggestion, onReject } = this.props
    const { id } = suggestion;

    let url = `game-suggestion/reject/${id}`
    if (['square_poster', 'large_poster'].includes(field)) {
      url = `game-suggestion/reject-image/${id}`
    }

    Api.post({ url })
      .then(() => {
        onReject(id, altField)
      })
      .catch(err => {
        captureFetchException(err)
        showToast({
          type: 'error',
          text: 'Не удалось отклонить предложение. Попробуй позже.',
        })
      })
  }

  acceptAndAppend = () => {
    this.accept(true)
  }

  renderButtons = () => {
    let acceptButtons = <></>;

    if (this.props.isEditor) {
      acceptButtons = <button
        type='button'
        className={cn(buttonsCss['primary'], buttonsCss['small'])}
        onClick={this.accept}
      >
        ОK
      </button>;

      if (this.props.field === 'GameName') {
        acceptButtons = <>
          <button
            type='button'
            className={cn(buttonsCss['primary'], buttonsCss['small'])}
            onClick={this.accept}
          >
            Заменить основное
          </button>
          <button
            type='button'
            className={cn(buttonsCss['primary'], buttonsCss['small'])}
            onClick={this.acceptAndAppend}
          >
            Добавить новое
          </button>
        </>
      }
    }

    return <div style='display: flex; align-items:center; column-gap: 13px'>
      {acceptButtons}
      <button
        type='button'
        className={cn(buttonsCss['icon'], buttonsCss['icon--low-contrast'])}
        style='--icon-size: 32px'
        onClick={this.reject}
      >
        <SVG i='sg-new/cross' adaptive/>
      </button>
    </div>
  }

  render() {
    const { suggestion, isEditor, showCredibility } = this.props

    return <div className={formCss['game-suggestion']} style=''>
      <div className={cn(formCss['game-suggestion__user'], {
        [formCss['game-suggestion__user--in-team']]: suggestion.user.inTeam
      })}>
        <img src={suggestion.user.avatar} alt=''/>
        <a target='_blank' href={suggestion.user.url}>{suggestion.user.name}</a>
      </div>
      {showCredibility && <>({suggestion.user.credibility}%)</>}
      {this.renderVerb()}
      {this.renderValue()}
      {(isEditor || suggestion.isOwner) && !suggestion.noAction ? this.renderButtons() : null}
    </div>
  }
}
